import {
  ADD_CLIENT_SUCCESS,
  ADD_COMPANY_SUCCESS,
  Company,
  UPDATE_CLIENTS_SUCCESS,
} from 'src/store/clients/types';
import { appAPI, ApiTags, ApiMethods } from '.';
import { Client } from 'src/constants';
import { RootState } from 'src/store';
import { MODIFY_INTERNAL_USERS } from 'src/store/users/types';
import {
  addCompanyToClients,
  removeCompanyFromClients,
} from 'src/store/clients/actions';

type getCompaniesInput = {
  ignoreAccess?: boolean;
};

type ClientInput = {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
};
type CompanyInput = {
  name: string;
  clients: Array<ClientInput>;
};

export type UnprocessedClientResponse = {
  client: ClientInput;
  error: string;
};

type CreateCompanyResponse = {
  company: Company;
  createdClients: Client[];
  updatedClients: Client[];
  unprocessedClients: Array<UnprocessedClientResponse>;
};

export const companiesApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    createCompany: build.mutation<CreateCompanyResponse, CompanyInput>({
      query: (newCompany) => ({
        path: `/v1/companies`,
        method: ApiMethods.post,
        options: {
          body: newCompany,
        },
      }),
      invalidatesTags: [
        ApiTags.companies,
        ApiTags.clientCompanies,
        ApiTags.clientUsers,
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        try {
          const state = getState() as RootState;
          const currentUserId = state.user.id;
          const { internalUsers } = state.users;
          const { data } = await queryFulfilled;
          if (data.createdClients.length) {
            dispatch({
              type: ADD_CLIENT_SUCCESS,
              payload: data.createdClients,
            });
          }
          if (data.updatedClients.length) {
            dispatch({
              type: UPDATE_CLIENTS_SUCCESS,
              payload: data.updatedClients,
            });
          }
          if (data.company) {
            dispatch({ type: ADD_COMPANY_SUCCESS, payload: [data.company] });
            dispatch({
              type: MODIFY_INTERNAL_USERS,
              payload: internalUsers.map((user) => {
                if (user.id === currentUserId) {
                  return {
                    ...user,
                    companyAccessList: [
                      ...(user.companyAccessList ?? []),
                      data.company,
                    ],
                  };
                }
                return user;
              }),
            });
          }
        } catch (e) {
          // nothing for now
        }
      },
    }),
    getCompanies: build.query<Company[], getCompaniesInput | undefined>({
      query: (getCompaniesInput) => ({
        path: `/v1/companies`,
        method: ApiMethods.get,
        options: {
          queryStringParameters: getCompaniesInput,
        },
      }),
      providesTags: [ApiTags.clientCompanies],
    }),
    manageCompanyClients: build.mutation<
      {
        addedClientIds: string[];
        addClientErrors: string[];
        removedClientIds: string[];
        removeClientErrors: string[];
      },
      { companyId: string; addClientIds: string[]; removeClientIds: string[] }
    >({
      query: ({ companyId, addClientIds, removeClientIds }) => ({
        path: `/v1/companies/${companyId}/clients`,
        method: ApiMethods.post,
        options: { body: { addClientIds, removeClientIds } },
      }),
      async onQueryStarted(
        { addClientIds, removeClientIds, companyId },
        { dispatch, queryFulfilled },
      ) {
        if (addClientIds.length > 0) {
          dispatch(addCompanyToClients(addClientIds, companyId));
        }
        if (removeClientIds.length > 0) {
          dispatch(removeCompanyFromClients(removeClientIds, companyId));
        }

        const updateClient = (
          clientIds: string[],
          action: 'add' | 'remove',
        ) => {
          if (action === 'add') {
            dispatch(addCompanyToClients(clientIds, companyId));
          } else {
            dispatch(removeCompanyFromClients(clientIds, companyId));
          }
        };

        // update the client state with response payload.
        // the response contains the ids of the added/removed clients.
        // if the client update is successful, we're good since the state is updated optimistically.
        // if the client update is not successful, we revert the optimistically updated client state. Unfortunatley,
        // we cannot leverage the rtk patch undo function as the clients state is not stored in the rtk query cache.
        try {
          const response = await queryFulfilled;
          if (response.data.addClientErrors.length > 0) {
            // remove the errored clients from the state
            updateClient(response.data.addClientErrors, 'remove');
          }

          if (response.data.removeClientErrors.length > 0) {
            // add back the clients that were removed but errored
            updateClient(response.data.removeClientErrors, 'add');
          }
        } catch (e) {
          // revert the optimistically updated client state.
          if (addClientIds.length > 0) {
            dispatch(removeCompanyFromClients(addClientIds, companyId));
          }
          if (removeClientIds.length > 0) {
            dispatch(addCompanyToClients(removeClientIds, companyId));
          }
        }
      },
    }),
    getClientCompanies: build.query<Company[], void>({
      query: () => ({
        path: `/v1/companies/client-companies`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: [ApiTags.clientCompanies],
    }),
  }),
});

export const {
  useGetCompaniesQuery,
  useCreateCompanyMutation,
  useManageCompanyClientsMutation,
  useGetClientCompaniesQuery,
} = companiesApi;
